<template>
  <UILoadingScreen :loading="saving" class="noprint" />
  <section ref="wrapper">
    <section
      id="scenario-header-wrapper"
      ref="header"
      class="wrapper"
      :class="{
        'shadow-4 shrink': shouldHeaderShrink,
        'relative shadow-2': !shouldHeaderShrink
      }"
    >
      <div
        class="scenario-header noprint"
        :class="{ container: !shouldHeaderShrink }"
      >
        <div class="tabs">
          <router-link
            v-for="(item, i) in menuItems"
            :key="item.label"
            :to="item.to"
            :class="{ active: i === activeMenuIndex, disabled: saving }"
          >
            <div class="menu-item">
              {{ item.label }}
            </div>
          </router-link>
        </div>

        <QuoteVersionDropDown :quote="quote" :saveQuote="saveQuote"/>
        <QuoteStatusDropDown :quote="quote" :saveQuote="saveQuote" :advancedSearch="advancedSearch" />
        <Button
          :label="(saving || loading) ? 'Saving ' : (isNewQuote ? 'Save as New' : 'Save')"
          class="p-button p-component ml-2 p-button-sm p-button-info"
          @click="isNewQuote ? saveAsNewQuote(true) : saveQuote(true)"
          :disabled="isNewQuote ? (saving || loading || !advancedSearch.quoteSearch) : (saving || loading || quoteIsWonOrRejected)"
          :icon="(saving || loading) ? 'loading-spinner' : ''"
        />
        <Button
          label="Print"
          class="p-button p-component ml-2 p-button-sm p-button-info"
          tooltip="Save to PDF"
          @click="printQuote"
          :disabled="isNewQuote"
        />
        <Button
          label="Export"
          icon="pi pi-file-excel"
          class="p-button p-component ml-2 p-button-sm p-button-success"
          tooltip="Export to Excel"
          :disabled="isNewQuote || saving || loading"
          @click="exportQuote"
        />
      </div>
    </section>
  </section>
</template>

<script lang="ts" setup>
import { ref, toRefs, computed, PropType } from "vue";
import { useRoute, useRouter } from "vue-router";
import { useWindowScroll } from "@vueuse/core";
import { statuses } from "@/utils/quoteHelpers";
import { useToast } from "primevue/usetoast";
import {
  AllQuoteFieldsFragment as QuoteType,
} from "gql/api";

import QuoteVersionDropDown from "@/components/quotes/QuoteVersionDropDown.vue";
import QuoteStatusDropDown from "@/components/quotes/QuoteStatusDropDown.vue";
import UILoadingScreen from "@/components/UILoadingScreen.vue";
import { exportQuoteToExcel } from "@/utils/exports/exportQuoteXlsx";


// NONREACTIVES:
const toast = useToast();
const { y: pageScrollY } = useWindowScroll();
const printQuote = async () => {
  if (isNewQuote.value) return;
  
  try {
    saving.value = true;
    await props.saveQuote();
  } catch (error) {
    console.error('Error saving quote before print:\n', error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to save quote before printing",
      life: 10000
    });
  } finally {
    saving.value = false;
    const prevDocTitle = document.title;
    document.title = `${quote?.value?.name} - Spyglass`;
    window.print();
    document.title = prevDocTitle;
  }
};

const exportQuote = () => {
  if (isNewQuote.value || saving.value || props.loading || !quote?.value) return;
  try {
    exportQuoteToExcel(quote.value);
  } catch (error) {
    console.error('Error exporting quote to Excel:\n', error);
    toast.add({
      severity: "error",
      summary: "Error",
      detail: "Failed to export quote to Excel",
      life: 10000
    });
  }
};

// PROPS:
// (TODO: refetchQuote is unused here, will probably be needed later)
const props = defineProps({
  quote: {
    type: Object as PropType<QuoteType>,
    default: null
  },
  advancedSearch: {
    type: Object,
    default: {}
  },
  saveQuote: {
    type: Function,
    required: true
  },
  saveAsNewQuote: {
    type: Function,
    required: true
  },
  refetchQuote: {
    type: Function,
    required: true
  },
  loading: {
    type: Boolean,
    required: false,
    default: false
  }
})

// TOREFS:
const { quote, advancedSearch } = toRefs(props)

// REACTIVES:
const route = useRoute();
const router = useRouter();

// REACTIVES - REFS:
const saving = ref<boolean>(false);

// REACTIVES - COMPUTEDS:
const shouldHeaderShrink = computed(() => pageScrollY.value > 100);
const quoteId = computed(() => route.params?.quoteId );
const isNewQuote = computed(() => !quoteId.value );
const quoteIsWonOrRejected = computed(() => {
  return [statuses.won, statuses.rejected].some(
    status => status === quote?.value?.status
  );
});
const menuItems = computed(() => [
  {
    label: "Sales Info",
    to: {
      name: quoteId.value ? "EditQuoteSalesInfo" : "QuoteNewSalesInfo",
      params: {quoteId: quoteId.value}
    },
    disabled: false
  },
  {
    label: "Quote",
    to: {
      name: quoteId.value ? "EditQuoteQuote" : "QuoteNewQuote",
      params: {quoteId: quoteId.value}
    },
    disabled: false
  },
])
const activeMenuIndex = computed(() =>
  menuItems.value.findIndex(item => item.to.name === route.name)
);

// NONREACTIVES - FUNCTIONS:
const saveQuote = (showToast: boolean = false) => {
  saving.value = true;
  return props.saveQuote()
    .then(() => {
      if (showToast) {
        toast.add({
          severity: "success",
          summary: "Success",
          detail: "Quote Update Successful",
          life: 10000
        });
      }
    })
    .catch((resp: any) => {
      saving.value = false;
      console.error('Error saving quote:\n', resp)
    })
    .finally(() => {
      saving.value = false;
    });
};

const saveAsNewQuote = (showToast: boolean = false) => {
  /* TODO: force open side panel IF AND ONLY IF on quote tab.
           The Save as New button will always be present regardless
           of which tab the user is on, so we need to take this into
           account.
     TODO: Users have reported a bug with the side panel force opening
           when not needed. This may or may not be an influence.
  */
  // openPanelUpdateEvent(quoteIdResp);
  saving.value = true;
  props.saveAsNewQuote()
    .finally(() => {
      if (showToast) {
        toast.add({
          severity: "success",
          summary: "Success",
          detail: "Quote Update Successful",
          life: 10000
        });
      }
      saving.value = false;
    })
    .catch((resp: any) => {
      saving.value = false;
      console.error('Error saving quote as new:\n', resp)
    });
}

// TODOs:

// 1) Implement delete draft button
// const { executeMutation: updateQuote } =
//   useUpdateQuoteByPkMutation();
// const deleteDraftClicked = (evt: MouseEvent) => {
//   if (!quote?.value?.id) {
//     // must exist a quote id in order to delete one
//     return;
//   }
//   return updateQuote({
//     id: quote?.value?.id,
//     object: { deleted_at: "now()" },
//     create_line_items: [],
//     update_line_items: []
//   })
//   .then(resp => {
//     const routeData = router.push({
//       name: "ListQuotes",
//       params: {
//         quoteId: quoteId.value
//       },
//       query: {
//         page: 1
//       }
//     });
//     // window.open(routeData.href + "?page=1", "_blank");
//   });
// };
</script>

<style lang="scss" scoped>
@media print {
  .wrapper {
    display: none;
  }
}

.wrapper {
  z-index: 2;
  position: relative;
  padding: 0;
  background-color: var(--gray-300);
  transition: all 0.2s ease-in-out;

  .scenario-header {
    position: relative;
    padding: 0.5em 0;
    justify-content: flex-start;
    display: grid;
    grid-gap: 0.25em;
    grid-auto-flow: dense;
    align-items: center;
    grid-auto-rows: auto;
    grid-template-columns: max-content 35% 1fr max-content max-content max-content;
    grid-template-areas: "tabs version status save print export";

    @include bp("mobile") {
      grid-template-columns: 100%;
      grid-template-areas: "tabs";
    }

    @include bp("touch") {
      grid-template-columns: 100%;
      grid-template-areas: "tabs";
    }

    @include bp("desktop") {
      grid-template-columns: max-content 35% 1fr max-content max-content max-content;
      grid-template-areas: "tabs version status save print export";
    }

    @include bp("hd") {
      grid-template-columns: max-content 35% 1fr max-content max-content max-content;
      grid-template-areas: "tabs version status save print export";
    }

    @include bp("wide") {
      grid-template-columns: max-content 45% 1fr max-content max-content max-content;
      grid-template-areas: "tabs version status save print export";
    }

    .tabs {
      grid-area: tabs;
      margin-block-start: 0;
      margin-block-end: 0;
      flex-shrink: 1;
      gap: 0.25em;
      display: flex;
      justify-content: space-between;
      min-width: max-content;
      align-items: center;
      padding: 0.5em;
      border-radius: 0.5em;
      border: solid 1px var(--surface-500);
      background-color: var(--surface-50);

      a {
        user-select: none;
        list-style: none;
        text-align: center;
        flex-grow: 1;
        padding: 0.25em 1em;
        border-radius: 0.25em;

        &.disabled {
          pointer-events: none;
          .menu-item {
            color: var(--surface-400);
          }
        }

        &:hover {
          outline: solid 1px var(--surface-500);
          text-decoration: none;
        }

        .menu-item {
          color: var(--text-color);
        }

        &.active {
          border-radius: 0.25em;
          background-color: var(--danger-500);
          .menu-item {
            color: white;
          }
        }
      }
    }
    .version {
      grid-area: version;
    }
    .status {
      grid-area: status;
    }
    .save {
      grid-area: save;
    }
    .print {
      grid-area: print;
    }
    .export {
      grid-area: export;
    }
  }
  &.shrink {
    margin: 0;
    position: sticky;
    top: -1px;
    padding: 0.5em 3em;
  }
}
.only-rows {
  :deep(.panel-row) {
    padding: 1em;
    background: var(--surface-100);
    gap: 0.25em;
    grid-template-columns: repeat(auto-fill, minmax(150px, 100%));
    transition: border-color 0.2s ease-in-out;
  }

  :deep(.equipment-header) {
    // &.has-extra {
    //   background: lightcoral;
    //   grid-template-columns: minmax(20px, max-content) minmax(100px, 1fr), 1fr;
    // }

    .extra {
      grid-column: 1/-1;
      grid-row: 2;
    }
    .right {
      grid-column: 2/-1;
    }
  }
}

#rightSplitterPanel {
  overflow: initial !important;
}

.splitter-right {
  position: sticky;
  overflow-x: hidden;
  overflow-y: scroll;
  scrollbar-gutter: stable;
  padding: 1em;
}
.equipment-section {
  transition: all 0.2s ease-in-out;

  .hidden {
    position: absolute;
    display: none;
  }
  .scroll-label {
    cursor: pointer;
    user-select: none;
    padding-left: 0.325em;
    text-decoration: underline;
    transition: all 0.2s ease-in-out;

    &:hover {
      font-weight: 700;
    }
  }

  .calcrow {
    width: 100%;
    min-height: 100%;
    display: grid;
    column-gap: 1em;
    row-gap: 0.125em;
    grid-template-columns: repeat(auto-fit, minmax(min-content - 1em, 1fr));
    transition: all 0.2s ease-in-out;
    :deep(.convert-mode) {
      border: 1px solid var(--danger-500);
    }
  }
}

@media only screen and (max-width: 750px) {
  .wrapper {
    .scenario-header {
      display: initial;
      .tabs {
        min-width: initial;
        flex-wrap: wrap;
        margin-bottom: 14px;
      }
    }
    &.shrink {
      padding: 0;
    }
  }
  .flex {
    justify-content: center;
    margin-top: 5px;
  }
  .toggle {
    justify-content: center !important;
  }
}
.toggle {
  justify-content: flex-end;
}

:deep {
  .loading-spinner {
    border: 2px solid $spyglass-blue;
    border-top: 2px solid var(--surface-400);
    border-radius: 50%;
    width: 20px;
    height: 20px;
    animation: spin 2s linear infinite;
    display: inline-block;
  }

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
}

</style>
